import { matchFilterString } from '@domain-group/javascript-utils'

import {
  isAdmin, isAgencyUser, isBrandUser, isProviderUser,
} from '@functions/user'

import type { NotificationModel, UserModel } from '@models/types'

export const matchAudience = (audience: string | undefined, roleTitle: string) => {
  if (audience === 'global') return true
  if (!!audience && isAdmin(roleTitle)) return true
  if (audience === 'agencies') return isAgencyUser(roleTitle) || isBrandUser(roleTitle)
  if (audience === 'brands') return isBrandUser(roleTitle)
  if (audience === 'providers') return isProviderUser(roleTitle)

  return false
}

type CheckUserNotificationParams = {
  currentUser: UserModel,
  notification: NotificationModel,
  roleTitle: string,
}

export const shouldDisplayNotification = (params: CheckUserNotificationParams) => {
  const { currentUser, notification, roleTitle } = params

  const {
    audience, creator_id, creator_type, notifiable_id, notifiable_type,
  } = notification
  const { association_id, association_type, brand_id } = currentUser

  if (matchAudience(audience, roleTitle)) return true
  if (notifiable_type === association_type && notifiable_id === association_id) return true
  if (notifiable_type === 'Brand' && notifiable_id === brand_id) return true
  if (isAdmin(roleTitle) && creator_id === 1 && creator_type === 'System') return true

  return false
}

export const getNotifiableTypeFromAudience = (audience: NotificationModel['audience']) => {
  switch (audience){
    case 'agencies':
      return 'Agency'
    case 'brands':
      return 'Brand'
    case 'providers':
      return 'Provider'
    default:
      return ''
  }
}

export const getAudience = (notifiableType: 'Agency' | 'Brand' | 'Provider') => {
  const audiences = {
    Agency: 'agencies',
    Brand: 'brands',
    Provider: 'providers',
  }

  return audiences[notifiableType] || ''
}

export const locationOptions = [
  { id: 'admin.helpdesk.dashboard.banner', title: 'Admin/HelpDesk/Dashboard', group: 'Banner' },
  { id: 'admin.trainingDashboard.dashboard.banner', title: 'Admin/TrainingDashboard/Dashboard', group: 'Banner' },
  { id: 'admin.trainingDashboard.dashboard.sidebar', title: 'Admin/TrainingDashboard/Dashboard', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.auctions.banner', title: 'Agency/AgencyDashboard/Auctions', group: 'Banner' },
  { id: 'agency.agencyDashboard.auctions.sidebar', title: 'Agency/AgencyDashboard/Auctions', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.bookings.banner', title: 'Agency/AgencyDashboard/Bookings', group: 'Banner' },
  { id: 'agency.agencyDashboard.bookings.sidebar', title: 'Agency/AgencyDashboard/Bookings', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.campaigns.banner', title: 'Agency/AgencyDashboard/Campaigns', group: 'Banner' },
  { id: 'agency.agencyDashboard.campaigns.sidebar', title: 'Agency/AgencyDashboard/Campaigns', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.deadlines.banner', title: 'Agency/AgencyDashboard/Deadlines', group: 'Banner' },
  { id: 'agency.agencyDashboard.deadlines.sidebar', title: 'Agency/AgencyDashboard/Deadlines', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.inspections.banner', title: 'Agency/AgencyDashboard/Inspections', group: 'Banner' },
  { id: 'agency.agencyDashboard.inspections.sidebar', title: 'Agency/AgencyDashboard/Inspections', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.orders.banner', title: 'Agency/AgencyDashboard/Orders', group: 'Banner' },
  { id: 'agency.agencyDashboard.orders.sidebar', title: 'Agency/AgencyDashboard/Orders', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.portalSyncs.banner', title: 'Agency/AgencyDashboard/Portal Syncs (Edit)', group: 'Banner' },
  { id: 'agency.agencyDashboard.portalSyncs.sidebar', title: 'Agency/AgencyDashboard/Portal Syncs (Edit)', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.quotes.banner', title: 'Agency/AgencyDashboard/Quotes', group: 'Banner' },
  { id: 'agency.agencyDashboard.quotes.sidebar', title: 'Agency/AgencyDashboard/Quotes', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.signboards.banner', title: 'Agency/AgencyDashboard/Signboards', group: 'Banner' },
  { id: 'agency.agencyDashboard.signboards.sidebar', title: 'Agency/AgencyDashboard/Signboards', group: 'Sidebar' },
  { id: 'agency.agencyDashboard.workflowTasks.banner', title: 'Agency/AgencyDashboard/Workflow Tasks', group: 'Banner' },
  { id: 'agency.agencyDashboard.workflowTasks.sidebar', title: 'Agency/AgencyDashboard/Workflow Tasks', group: 'Sidebar' },
  { id: 'agency.agencyManager.assetLibraries.banner', title: 'Agency/AgencyManager/AssetLibraries', group: 'Banner' },
  { id: 'agency.agencyManager.assetLibraries.sidebar', title: 'Agency/AgencyManager/AssetLibraries', group: 'Sidebar' },
  { id: 'agency.agencyManager.campaignContributions.banner', title: 'Agency/AgencyManager/CampaignContributions', group: 'Banner' },
  { id: 'agency.agencyManager.campaignContributions.sidebar', title: 'Agency/AgencyManager/CampaignContributions', group: 'Sidebar' },
  { id: 'agency.agencyManager.campaignStatement.banner', title: 'Agency/AgencyManager/CampaignStatement', group: 'Banner' },
  { id: 'agency.agencyManager.campaignStatement.sidebar', title: 'Agency/AgencyManager/CampaignStatement', group: 'Sidebar' },
  { id: 'agency.agencyManager.caseStudies.banner', title: 'Agency/AgencyManager/CaseStudies', group: 'Banner' },
  { id: 'agency.agencyManager.caseStudies.sidebar', title: 'Agency/AgencyManager/CaseStudies', group: 'Sidebar' },
  { id: 'agency.agencyManager.customDeadlines.banner', title: 'Agency/AgencyManager/CustomDeadlines', group: 'Banner' },
  { id: 'agency.agencyManager.customDeadlines.sidebar', title: 'Agency/AgencyManager/CustomDeadlines', group: 'Sidebar' },
  { id: 'agency.agencyManager.customItems.banner', title: 'Agency/AgencyManager/CustomItems', group: 'Banner' },
  { id: 'agency.agencyManager.customItems.sidebar', title: 'Agency/AgencyManager/CustomItems', group: 'Sidebar' },
  { id: 'agency.agencyManager.details.banner', title: 'Agency/AgencyManager/Details', group: 'Banner' },
  { id: 'agency.agencyManager.details.sidebar', title: 'Agency/AgencyManager/Details', group: 'Sidebar' },
  { id: 'agency.agencyManager.editUser.banner', title: 'Agency/AgencyManager/Users (Edit)', group: 'Banner' },
  { id: 'agency.agencyManager.editUser.sidebar', title: 'Agency/AgencyManager/Users (Edit)', group: 'Sidebar' },
  { id: 'agency.agencyManager.editWorkflow.banner', title: 'Agency/AgencyManager/Workflows (Edit)', group: 'Banner' },
  { id: 'agency.agencyManager.editWorkflow.sidebar', title: 'Agency/AgencyManager/Workflows (Edit)', group: 'Sidebar' },
  { id: 'agency.agencyManager.imageLibraries.banner', title: 'Agency/AgencyManager/ImageLibraries', group: 'Banner' },
  { id: 'agency.agencyManager.imageLibraries.sidebar', title: 'Agency/AgencyManager/ImageLibraries', group: 'Sidebar' },
  { id: 'agency.agencyManager.imageSettings.banner', title: 'Agency/AgencyManager/ImageSettings', group: 'Banner' },
  { id: 'agency.agencyManager.imageSettings.sidebar', title: 'Agency/AgencyManager/ImageSettings', group: 'Sidebar' },
  { id: 'agency.agencyManager.integrations.banner', title: 'Agency/AgencyManager/Integrations', group: 'Banner' },
  { id: 'agency.agencyManager.integrations.sidebar', title: 'Agency/AgencyManager/Integrations', group: 'Sidebar' },
  { id: 'agency.agencyManager.invoiceImport.banner', title: 'Agency/AgencyManager/InvoiceImport', group: 'Banner' },
  { id: 'agency.agencyManager.invoiceImport.sidebar', title: 'Agency/AgencyManager/InvoiceImport', group: 'Sidebar' },
  { id: 'agency.agencyManager.invoices.banner', title: 'Agency/AgencyManager/Invoices', group: 'Banner' },
  { id: 'agency.agencyManager.invoices.sidebar', title: 'Agency/AgencyManager/Invoices', group: 'Sidebar' },
  { id: 'agency.agencyManager.manageProducts.banner', title: 'Agency/AgencyManager/ManageProducts', group: 'Banner' },
  { id: 'agency.agencyManager.manageProducts.sidebar', title: 'Agency/AgencyManager/ManageProducts', group: 'Sidebar' },
  { id: 'agency.agencyManager.orderBundles.banner', title: 'Agency/AgencyManager/OrderBundles', group: 'Banner' },
  { id: 'agency.agencyManager.orderBundles.sidebar', title: 'Agency/AgencyManager/OrderBundles', group: 'Sidebar' },
  { id: 'agency.agencyManager.orderReconciliation.banner', title: 'Agency/AgencyManager/OrderReconciliation', group: 'Banner' },
  { id: 'agency.agencyManager.orderReconciliation.sidebar', title: 'Agency/AgencyManager/OrderReconciliation', group: 'Sidebar' },
  { id: 'agency.agencyManager.priceLists.banner', title: 'Agency/AgencyManager/PriceLists', group: 'Banner' },
  { id: 'agency.agencyManager.priceLists.sidebar', title: 'Agency/AgencyManager/PriceLists', group: 'Sidebar' },
  { id: 'agency.agencyManager.providerPackages.banner', title: 'Agency/AgencyManager/ProviderPackages', group: 'Banner' },
  { id: 'agency.agencyManager.providerPackages.sidebar', title: 'Agency/AgencyManager/ProviderPackages', group: 'Sidebar' },
  { id: 'agency.agencyManager.reporting.banner', title: 'Agency/AgencyManager/Reporting', group: 'Banner' },
  { id: 'agency.agencyManager.reporting.sidebar', title: 'Agency/AgencyManager/Reporting', group: 'Sidebar' },
  { id: 'agency.agencyManager.savedArtworks.banner', title: 'Agency/AgencyManager/SaveArtworks', group: 'Banner' },
  { id: 'agency.agencyManager.savedArtworks.sidebar', title: 'Agency/AgencyManager/SaveArtworks', group: 'Sidebar' },
  { id: 'agency.agencyManager.settings.banner', title: 'Agency/AgencyManager/Settings', group: 'Banner' },
  { id: 'agency.agencyManager.settings.sidebar', title: 'Agency/AgencyManager/Settings', group: 'Sidebar' },
  { id: 'agency.agencyManager.taskPricing.banner', title: 'Agency/AgencyManager/TaskPricing', group: 'Banner' },
  { id: 'agency.agencyManager.taskPricing.sidebar', title: 'Agency/AgencyManager/TaskPricing', group: 'Sidebar' },
  { id: 'agency.agencyManager.teams.banner', title: 'Agency/AgencyManager/Teams', group: 'Banner' },
  { id: 'agency.agencyManager.teams.sidebar', title: 'Agency/AgencyManager/Teams', group: 'Sidebar' },
  { id: 'agency.agencyManager.users.banner', title: 'Agency/AgencyManager/Users', group: 'Banner' },
  { id: 'agency.agencyManager.users.sidebar', title: 'Agency/AgencyManager/Users', group: 'Sidebar' },
  { id: 'agency.agencyManager.templateGallery.banner', title: 'Agency/AgencyManager/TemplateGallery', group: 'Banner' },
  { id: 'agency.agencyManager.templateGallery.sidebar', title: 'Agency/AgencyManager/TemplateGallery', group: 'Sidebar' },
  { id: 'agency.agencyManager.workflows.banner', title: 'Agency/AgencyManager/Workflows (List)', group: 'Banner' },
  { id: 'agency.agencyManager.workflows.sidebar', title: 'Agency/AgencyManager/Workflows (List)', group: 'Sidebar' },
  { id: 'agency.agencyMarketing.dashboard.banner', title: 'Agency/AgencyMarketing/Dashboard', group: 'Banner' },
  { id: 'agency.agencyMarketing.dashboard.sidebar', title: 'Agency/AgencyMarketing/Dashboard', group: 'Sidebar' },
  { id: 'agency.artworkBuilder.newPage.banner', title: 'Agency/ArtworkBuilder/NewPage', group: 'Banner' },
  { id: 'agency.artworkReviewer.campaign.banner', title: 'Agency/ArtworkReviewer/Campaign', group: 'Banner' },
  { id: 'agency.campaign.accounting.banner', title: 'Agency/Campaign/Accounting (Dashboard)', group: 'Banner' },
  { id: 'agency.campaign.accounting.sidebar', title: 'Agency/Campaign/Accounting (Dashboard)', group: 'Sidebar' },
  { id: 'agency.campaign.activity.banner', title: 'Agency/Campaign/Activity', group: 'Banner' },
  { id: 'agency.campaign.activity.sidebar', title: 'Agency/Campaign/Activity', group: 'Sidebar' },
  { id: 'agency.campaign.adCopy.banner', title: 'Agency/Campaign/AdCopy', group: 'Banner' },
  { id: 'agency.campaign.adCopy.sidebar', title: 'Agency/Campaign/AdCopy', group: 'Sidebar' },
  { id: 'agency.campaign.agreements.banner', title: 'Agency/Campaign/Agreements', group: 'Banner' },
  { id: 'agency.campaign.agreements.sidebar', title: 'Agency/Campaign/Agreements', group: 'Sidebar' },
  { id: 'agency.campaign.comparables.banner', title: 'Agency/Campaign/Comparables', group: 'Banner' },
  { id: 'agency.campaign.comparables.sidebar', title: 'Agency/Campaign/Comparables', group: 'Sidebar' },
  { id: 'agency.campaign.contacts.banner', title: 'Agency/Campaign/Contacts', group: 'Banner' },
  { id: 'agency.campaign.contacts.sidebar', title: 'Agency/Campaign/Contacts', group: 'Sidebar' },
  { id: 'agency.campaign.conversations.banner', title: 'Agency/Campaign/Conversations', group: 'Banner' },
  { id: 'agency.campaign.conversations.sidebar', title: 'Agency/Campaign/Conversations', group: 'Sidebar' },
  { id: 'agency.campaign.dataStore.banner', title: 'Agency/Campaign/DataStore', group: 'Banner' },
  { id: 'agency.campaign.dataStore.sidebar', title: 'Agency/Campaign/DataStore', group: 'Sidebar' },
  { id: 'agency.campaign.details.banner', title: 'Agency/Campaign/Details', group: 'Banner' },
  { id: 'agency.campaign.details.sidebar', title: 'Agency/Campaign/Details', group: 'Sidebar' },
  { id: 'agency.campaign.forms.banner', title: 'Agency/Campaign/Forms', group: 'Banner' },
  { id: 'agency.campaign.forms.sidebar', title: 'Agency/Campaign/Forms', group: 'Sidebar' },
  { id: 'agency.campaign.media.banner', title: 'Agency/Campaign/Media', group: 'Banner' },
  { id: 'agency.campaign.media.sidebar', title: 'Agency/Campaign/Media', group: 'Sidebar' },
  { id: 'agency.campaign.mediaSelection.banner', title: 'Agency/Campaign/Media Selection', group: 'Banner' },
  { id: 'agency.campaign.mediaSelection.sidebar', title: 'Agency/Campaign/Media Selection', group: 'Sidebar' },
  { id: 'agency.campaign.microsites.banner', title: 'Agency/Campaign/Microsites', group: 'Banner' },
  { id: 'agency.campaign.microsites.sidebar', title: 'Agency/Campaign/Microsites', group: 'Sidebar' },
  { id: 'agency.campaign.orders.banner', title: 'Agency/Campaign/Orders', group: 'Banner' },
  { id: 'agency.campaign.orders.sidebar', title: 'Agency/Campaign/Orders', group: 'Sidebar' },
  { id: 'agency.campaign.overview.banner', title: 'Agency/Campaign/Overview', group: 'Banner' },
  { id: 'agency.campaign.overview.sidebar', title: 'Agency/Campaign/Overview', group: 'Sidebar' },
  { id: 'agency.campaign.portals.banner', title: 'Agency/Campaign/Portals', group: 'Banner' },
  { id: 'agency.campaign.portals.sidebar', title: 'Agency/Campaign/Portals', group: 'Sidebar' },
  { id: 'agency.campaign.proposals.banner', title: 'Agency/Campaign/Proposals', group: 'Banner' },
  { id: 'agency.campaign.proposals.sidebar', title: 'Agency/Campaign/Proposals', group: 'Sidebar' },
  { id: 'agency.campaign.quotes.banner', title: 'Agency/Campaign/Quotes', group: 'Banner' },
  { id: 'agency.campaign.quotes.sidebar', title: 'Agency/Campaign/Quotes', group: 'Sidebar' },
  { id: 'agency.campaign.workflow.banner', title: 'Agency/Campaign/Workflow', group: 'Banner' },
  { id: 'agency.campaign.workflow.sidebar', title: 'Agency/Campaign/Workflow', group: 'Sidebar' },
  { id: 'agency.campaigns.dashboard.banner', title: 'Agency/Campaigns/Dashboard', group: 'Banner' },
  { id: 'agency.digitalPages.agreements.banner', title: 'Agency/DigitalPages/Agreements', group: 'Banner' },
  { id: 'agency.digitalPages.agreements.sidebar', title: 'Agency/DigitalPages/Agreements', group: 'Sidebar' },
  { id: 'agency.digitalPages.details.banner', title: 'Agency/DigitalPages/Details', group: 'Banner' },
  { id: 'agency.digitalPages.details.sidebar', title: 'Agency/DigitalPages/Details', group: 'Sidebar' },
  { id: 'agency.digitalPages.forms.banner', title: 'Agency/DigitalPages/Forms', group: 'Banner' },
  { id: 'agency.digitalPages.forms.sidebar', title: 'Agency/DigitalPages/Forms', group: 'Sidebar' },
  { id: 'agency.digitalPages.microsites.banner', title: 'Agency/DigitalPages/Microsites', group: 'Banner' },
  { id: 'agency.digitalPages.microsites.sidebar', title: 'Agency/DigitalPages/Microsites', group: 'Sidebar' },
  { id: 'agency.mediaReviewer.campaign.banner', title: 'Agency/MediaReviewer/Campaign', group: 'Banner' },
  { id: 'agency.mediaReviewer.campaignPublic.banner', title: 'Agency/MediaReviewer/CampaignPublic', group: 'Banner' },
  { id: 'agency.proposals.dashboard.banner', title: 'Agency/Proposals/Dashboard', group: 'Banner' },
  { id: 'agency.proposals.dashboard.sidebar', title: 'Agency/Proposals/Dashboard', group: 'Sidebar' },
  { id: 'brand.helpdesk.dashboard.banner', title: 'Brand/HelpDesk/Dashboard', group: 'Banner' },
  { id: 'provider.helpdesk.dashboard.banner', title: 'Provider/HelpDesk/Dashboard', group: 'Banner' },
  { id: 'provider.orders.dashboard.banner', title: 'Provider/Orders/Dashboard', group: 'Banner' },
]

export default locationOptions

type FilterSectionsParams = {
  group: 'Banner' | 'Sidebar',
  userType: '' | 'agency' | 'brand' | 'provider',
}

export const findUniqueSectionTitles = (params: FilterSectionsParams) => {
  const { group, userType } = params

  const sections: string[] = []

  locationOptions.forEach((location) => {
    const { group: locationGroup, id, title } = location

    const groupMatch = group === locationGroup

    const locationUserType = id.split('.')[0]
    const userTypeMatch = matchFilterString(locationUserType, userType, { exact: true })

    const sectionTitle = title.split('/')[1]

    if (groupMatch && userTypeMatch && !sections.includes(sectionTitle)) sections.push(sectionTitle)
  })

  return sections
}

type FilterNotificationsByLocationParams = {
  group: 'Banner' | 'Sidebar',
  section: string,
  userType: '' | 'agency' | 'brand' | 'provider',
}

export const filterNotificationsByLocation = (params: FilterNotificationsByLocationParams) => {
  const { group, section, userType } = params

  return locationOptions.filter((location) => {
    const { group: locationGroup, id, title } = location

    const groupMatch = group === locationGroup
    const locationUserType = id.split('.')[0]
    const userTypeMatch = matchFilterString(locationUserType, userType, { exact: true })

    const sectionTitle = title.split('/')[1]
    const sectionTitleMatch = matchFilterString(sectionTitle, section, { exact: true })

    return groupMatch && sectionTitleMatch && userTypeMatch
  })
}

export const notificationTypes = {
  artwork: [
    {
      editable: true,
      key: 'artwork_commented_email',
      title: 'New Comment',
      user_types: ['Agent', 'OfficeAdministrator'],
    },
    {
      editable: true,
      key: 'artwork_reviewed_email',
      title: 'New Review',
      user_types: ['Agent', 'OfficeAdministrator'],
    },
  ],
  bookings: [
    {
      editable: true,
      key: 'booking_created_email',
      title: 'Created',
      user_types: ['Agent', 'OfficeAdministrator'],
    },
    {
      editable: true,
      key: 'booking_updated_email',
      title: 'Updated',
      user_types: ['Agent', 'OfficeAdministrator'],
    },
    {
      editable: true,
      key: 'booking_cancelled_email',
      title: 'Cancelled',
      user_types: ['Agent', 'OfficeAdministrator'],
    },
  ],
  conversations: [
    {
      editable: true,
      key: 'new_conversation_comment',
      title: 'New Comment',
      user_types: ['Agent', 'OfficeAdministrator'],
    },
  ],
  orders: [
    {
      editable: true,
      key: 'order_confirmation_email',
      title: 'Confirmation',
      user_types: ['Agent', 'OfficeAdministrator'],
    },
    {
      editable: true,
      key: 'order_item_dispatched_email',
      title: 'Dispatched',
      user_types: ['Agent'],
    },
  ],
  tasks: [
    {
      editable: true,
      key: 'task_completion_email',
      title: 'Complete',
      user_types: ['Agent', 'OfficeAdministrator'],
    },
  ],
}

export const filterNotificationTypes = (actableType: string) => {
  const filteredNotificationTypes: Partial<typeof notificationTypes> = {}

  Object.keys(notificationTypes).forEach((category) => {
    // @ts-expect-error TS7053
    const notificationItems = notificationTypes[category].filter(({ user_types = [] }) => (
      // @ts-expect-error TS2345
      user_types.includes(actableType)
    ))

    if (notificationItems.length > 0){
      // @ts-expect-error TS7053
      filteredNotificationTypes[category] = notificationItems
    }
  })

  return filteredNotificationTypes
}
